<template>
    <v-data-table
        :headers="headers"
        :items="addressCollection.models"
        :loading="loading"
        hide-default-footer
        disable-pagination
        class="elevation-1"
    >
        <template #[`item.context_menu`]="{ item }">
            <v-menu offset-y>
                <template #activator="{ on, attrs }">
                    <v-btn dark icon v-bind="attrs" v-on="on">
                        <KpIcon icon-cls="moredots" color="grey" />
                    </v-btn>
                </template>

                <AddressContactContextMenu
                    :record="item"
                    :hide-delete-link="hideDeleteLink"
                    :hide-edit-contact="hideEditContact"
                    @edit-item="$emit('edit-item', item)"
                    @edit-contact="$emit('edit-contact', item)"
                    @delete-link="$emit('delete-link', item)"
                />
            </v-menu>
        </template>

        <template #[`item.adresse_typ`]="{ item }">
            <TypIcon :address-record="item" />
            <LegalTypeIcon :address-record="item" />
            <KpIcon
                v-if="item.addressContact && item.addressContact.is_main_contact"
                icon-cls="star"
                color="grey"
                :title="$t('main_contact')"
            />
            <KpIcon
                v-if="item.addressContact && item.addressContact.publish_frontend"
                icon-cls="published"
                color="ok"
                :title="$t('frontend_publish_contact')"
            />
        </template>

        <template #[`item.image`]="{ item }">
            <ListImageWithPrevLinks :address-record="item" />
        </template>
        <template #[`item.title`]="{ item }">
            <div>{{ item.title ? item.title : `${item.first_name} ${item.surname}` }}</div>
            <div v-if="item.addressContact && item.addressContact.relation_function">
                <hr />
                {{ item.addressContact.relation_function }}
            </div>
        </template>
        <template #[`item.contact`]="{ item }">
            <div>
                <a v-if="item.tel" :href="`tel:${item.tel}`">{{ item.tel }}</a>
            </div>
            <div>
                <a v-if="item.email" :href="`mailto:${item.email}`">{{ item.email }}</a>
            </div>
        </template>
    </v-data-table>
</template>

<script>
import { AddressCollection } from '@/models/Address';
import { AddressContactCollection } from '@/models/AddressContact';
import ListImageWithPrevLinks from '@/components/address/ListImageWithPrevLinks.vue';
import AddressContactContextMenu from '@/components/address/AddressContactContextMenu.vue';
import TypIcon from '@/components/address/TypIcon.vue';
import LegalTypeIcon from '@/components/address/LegalTypeIcon.vue';

export default {
    name: 'LinkedAddressList',
    components: { ListImageWithPrevLinks, TypIcon, LegalTypeIcon, AddressContactContextMenu },
    props: {
        filters: {
            type: Array,
            default: () => [],
        },
        queryParams: {
            type: Object,
            default: () => ({}),
        },
        hideDeleteLink: {
            type: Boolean,
            default: false,
        },
        hideEditContact: {
            type: Boolean,
            default: false,
        },
        /** The ID of the main address record to load the contacts for */
        loadAddressContactRecordsFor: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            loading: false,
            addressCollection: new AddressCollection(),
            headers: [
                { width: 35, sortable: false, value: 'context_menu' },
                { text: this.$t('relation'), value: 'adresse_typ', sortable: false },
                { text: 'Bild', value: 'image', width: 100, sortable: false },
                { text: this.$t('titel'), sortable: true, value: 'title' },
                { text: this.$t('contact'), value: 'contact', sortable: false },
            ],
        };
    },
    watch: {
        queryParams(newVal, oldVal) {
            if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                this.loadAddresses();
            }
        },
        filters(newVal, oldVal) {
            if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                this.loadAddresses();
            }
        },
    },

    mounted() {
        this.loadAddresses();
    },

    methods: {
        async loadAddresses() {
            // We load address in 2 variants:
            // 1. default: we load addresses directly, using a filter/query,
            // 2. we first load ContactAddress records, then the associated addresses,
            //    so that we can assign the contact link information to the addresses
            if (this.loadAddressContactRecordsFor) {
                return this.loadAddressesViaContacts();
            } else {
                // direct address loading:
                const params = { ...this.queryParams };
                this.filters.forEach((element) => {
                    this.addressCollection.setFilter(element.property, element.value, element.operator);
                });
                this.loading = true;
                await this.addressCollection.query(params);
                this.loading = false;
            }
        },

        /**
         * We need a list of addresses, but with the associated AddressContact information.
         * So we load them as follows:
         * 1. Load the AddressContact records for the given loadAddressContactRecordsFor id
         * 2. extract the "other" address IDs from the contact link records
         * 3. Load those "other" addresses by ID
         * 4. set the AddressContact record for each loaded address, so that we can
         *    render the relation function and have the Contact ID ready for un-linking.
         */
        async loadAddressesViaContacts() {
            if (!Number(this.loadAddressContactRecordsFor)) {
                this.addressCollection.clear();
                return;
            }
            // Load the AddressContact records:
            const contactCollection = new AddressContactCollection();
            const params = { ...this.queryParams, ...{ assigned_address_id: this.loadAddressContactRecordsFor } };
            this.filters.forEach((element) => {
                contactCollection.setFilter(element.property, element.value, element.operator);
            });
            this.loading = true;
            await contactCollection.query(params);

            // now we have the contact records, we now load the associated addresses
            const addressIds = [];
            this.addressCollection.clear();
            contactCollection.models.forEach((item) => {
                if (item.main_address_id !== this.loadAddressContactRecordsFor) {
                    addressIds.push(item.main_address_id);
                }
                if (item.contact_address_id !== this.loadAddressContactRecordsFor) {
                    addressIds.push(item.contact_address_id);
                }
            });

            if (addressIds.length) {
                this.addressCollection.setFilter('id', addressIds, 'IN');
                this.addressCollection.addExtraParam('disableAccessCheck', true);
                await this.addressCollection.query();
                // so we have the addresses, now we need to assign the contact records with them:
                contactCollection.each((contactRec) => {
                    const matchingAddress = this.addressCollection.find(
                        (address) =>
                            address.id === contactRec.main_address_id || address.id === contactRec.contact_address_id,
                    );

                    // We insert a virtual field on the Address Record: addressContact:
                    if (matchingAddress && !matchingAddress.addressContact) {
                        matchingAddress.addressContact = contactRec;
                    }
                });
            }

            this.loading = false;
        },
    },
};
</script>
